import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowRight, Sparkles, BarChart3, Brain, MessageSquare } from 'lucide-react';

const EVA_IMAGE = 'https://restaff.b-cdn.net/Eva/eva1.png';

const AI_PROVIDERS = [
  { name: 'OpenAI',    slug: 'openai',    icon: '/icons/openai-white.svg' },
  { name: 'Claude',    slug: 'claude',    icon: '/icons/claude-white.svg' },
  { name: 'Gemini',    slug: 'gemini',    icon: '/icons/gemini.svg' },
  { name: 'Grok',      slug: 'grok',      icon: '/icons/grok-white.svg' },
  { name: 'DeepSeek',  slug: 'deepseek',  icon: '/icons/deepseek.svg' },
  { name: 'Kimi',      slug: 'kimi',      icon: '/icons/kimi.svg' },
  { name: 'Z.ai',      slug: 'zai',       icon: '/icons/zai.svg' },
];

const EVA_CAPABILITIES = [
  {
    icon: BarChart3,
    title: 'Real-Time Analytics',
    desc: 'Get instant answers about labor costs, overtime risks, and budget utilization.',
  },
  {
    icon: Brain,
    title: 'Smart Recommendations',
    desc: 'Eva analyzes your data and suggests ways to tighten schedules and cut costs.',
  },
  {
    icon: MessageSquare,
    title: 'Natural Conversation',
    desc: 'Just ask in plain English — Eva understands your business context.',
  },
];

export function EvaAISection() {
  return (
    <section
      className="py-10 md:py-16 lg:py-28 bg-slate-900 relative overflow-hidden"
      id="eva-ai"
      aria-label="Eva AI Assistant"
      data-testid="eva-ai-section"
    >
      {/* Subtle background pattern */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Section header */}
        <div className="text-center mb-8 md:mb-12 lg:mb-20">
          <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 mb-6">
            <Sparkles size={14} className="text-orange-400" />
            <span className="text-xs font-semibold text-orange-400 uppercase tracking-wide">AI-Powered</span>
          </div>
          <h2
            className="text-4xl md:text-5xl font-bold text-white leading-tight"
            style={{ fontFamily: 'Manrope' }}
            data-testid="eva-ai-title"
          >
            Your AI Workforce Assistant
          </h2>
          <p
            className="mt-5 text-lg md:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed"
            data-testid="eva-ai-subtitle"
          >
            Meet Eva — your built-in AI that analyzes your business in real time and helps you make smarter decisions.
          </p>
        </div>

        {/* Hero screenshot */}
        <div className="mb-10 md:mb-16 lg:mb-20 max-w-4xl mx-auto">
          <div
            className="relative rounded-xl overflow-hidden border border-slate-700/50 shadow-2xl"
            data-testid="eva-screenshot"
          >
            <img
              src={EVA_IMAGE}
              alt="Eva AI assistant analyzing workforce data in real time"
              loading="lazy"
              width="960"
              height="620"
              className="w-full h-auto"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent pointer-events-none" />
          </div>
        </div>

        {/* Capabilities grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 lg:gap-8 mb-10 md:mb-14 lg:mb-16">
          {EVA_CAPABILITIES.map((cap) => {
            const Icon = cap.icon;
            return (
              <div
                key={cap.title}
                className="p-6 rounded-xl bg-slate-800/60 border border-slate-700/50 hover:border-orange-500/30 transition-colors duration-200"
                data-testid={`eva-cap-${cap.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center mb-4">
                  <Icon size={20} className="text-orange-400" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2" style={{ fontFamily: 'Manrope' }}>
                  {cap.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">{cap.desc}</p>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link to="/register">
            <Button
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white text-base px-10 py-3 rounded-full shadow-lg shadow-orange-500/20 transition-all duration-200 hover:shadow-xl hover:shadow-orange-500/30 hover:scale-105"
              data-testid="eva-ai-cta-btn"
            >
              <Sparkles size={18} className="mr-2" />
              Try Eva AI
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
          <p className="mt-5 text-xs md:text-sm font-semibold uppercase tracking-wider text-slate-500">
            Bring Your Own AI Key
          </p>
          <div className="mt-3 md:mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 md:gap-x-10 md:gap-y-4" data-testid="ai-providers-list">
            {AI_PROVIDERS.map((p) => (
              <div key={p.name} className="flex items-center gap-2 md:gap-3 text-slate-400 transition-all duration-200 hover:opacity-80 hover:scale-105" data-testid={`ai-provider-${p.slug}`}>
                <img src={p.icon} alt={p.name} className="w-5 h-5 md:w-8 md:h-8 flex-shrink-0" loading="lazy" />
                <span className="text-xs md:text-base">{p.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
